import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { initialState, reducer } from './state';
import type { AgentState, CallbackEntry, LoginResponse, Webphone } from './types';
import { Modal } from './components/Modal';
import { useWs } from './hooks/useWs';
import { useWebphone } from './hooks/useWebphone';
import { wantRoom } from './roomPolicy';
import { Login } from './components/Login';
import { ResetPassword } from './components/ResetPassword';
import { Navbar } from './components/Navbar';
import { LiveStatusBar } from './components/LiveStatusBar';
import { ContactControl } from './components/ContactControl';
import { LeadForm } from './components/LeadForm';
import { ScriptTab } from './components/ScriptTab';
import { FormsTab } from './components/FormsTab';
import { DispoScreen } from './components/DispoScreen';
import { CallbackScheduler } from './components/CallbackScheduler';
import { CallbacksList, CallLog, PauseCodePicker, TransferPanel, WrapupBox } from './components/Lists';
import { ManualDial } from './components/ManualDial';

// Only the short-lived bearer is stored, in sessionStorage (AE-10888): it survives a reload but not
// the tab, which costs nothing because a closed tab is reaped from routing within 15 s anyway
// (AE-10714). The SIP password and the rest of the login live in memory and come back from
// /api/session/refresh. Cleared on logout and whenever the BFF answers 401.
const TOKEN_KEY = 'agent-desktop.token';
// Pre-AE-10888 builds kept the whole login response, SIP password included, in localStorage.
const LEGACY_SESSION_KEY = 'agent-desktop.session';

/** --A--field--B-- merge into a web-form URL (AE-10743). */
function mergeWebForm(url: string, lead: { phone_number: string; data: Record<string, unknown> }): string {
  return url.replace(/--A--([a-z0-9_]+)--B--/gi, (_m, f: string) => encodeURIComponent(f === 'phone_number' ? lead.phone_number : String(lead.data[f] ?? '')));
}

export function App() {
  const [s, dispatch] = useReducer(reducer, initialState);
  const [webphone, setWebphone] = useState<Webphone | null>(null);
  const [tab, setTab] = useState<'contact' | 'script' | 'forms'>('contact');
  const [callbacks, setCallbacks] = useState<CallbackEntry[]>([]);
  const [agents, setAgents] = useState<Array<{ agent_id: string; state: string }>>([]);
  const [wrapLeft, setWrapLeft] = useState(0);
  const [showCallbackForm, setShowCallbackForm] = useState<string | null>(null);
  const token = s.session?.token ?? null;
  // Set from the Ready click until Pause/Logout, so the room join starts before agent-state says ready.
  const [readyIntent, setReadyIntent] = useState(false);
  const webphoneRef = useRef(s.webphone); webphoneRef.current = s.webphone;
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const dropSession = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setTokenExpiresAt(null);
    setWebphone(null);
    setReadyIntent(false);
    dispatch({ type: 'LOGOUT' });
  }, []);
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.status === 401) { dropSession(); return; }
    dispatch({ type: 'ERROR', message: e instanceof Error ? e.message : String(e) });
  };

  // ---- login / reconnect -------------------------------------------------
  const applyLogin = useCallback((body: LoginResponse) => {
    dispatch({ type: 'LOGIN_OK', body, now: Date.now() });
    setWebphone(body.webphone);
    setTokenExpiresAt(body.token_expires_at ?? null);
    sessionStorage.setItem(TOKEN_KEY, body.token);
  }, []);
  useEffect(() => {
    // One-time migration off the legacy blob: take its token, then delete it (it holds the SIP password).
    let legacy: Partial<LoginResponse> | null = null;
    const raw = localStorage.getItem(LEGACY_SESSION_KEY);
    if (raw) {
      localStorage.removeItem(LEGACY_SESSION_KEY);
      try { legacy = JSON.parse(raw) as Partial<LoginResponse>; } catch { legacy = null; }
    }
    const stored = sessionStorage.getItem(TOKEN_KEY) ?? (typeof legacy?.token === 'string' ? legacy.token : null);
    if (!stored) return;
    (async () => {
      const r = await api.refresh(stored);
      const me = await api.me(r.token);
      // A session from before the BFF kept wss_url has none to give back; the legacy blob does.
      const webphone = r.webphone && (r.webphone.wss_url || !legacy?.webphone) ? r.webphone : (legacy?.webphone ?? null);
      if (!webphone) throw new ApiError(401, 'no webphone identity for this session');
      applyLogin({ token: r.token, token_expires_at: r.token_expires_at, agent: { ...r.agent, state: me.agent.state as AgentState }, webphone, campaign: r.campaign });
      dispatch({ type: 'AGENT_STATE', state: me.agent.state as AgentState, pauseCode: me.agent.pause_reason ?? null, now: Date.now() });
    })().catch((e) => {
      // 401 = the session is gone (token expired, or agent-state reaped us while the tab was
      // closed). Anything else is transient: keep the stored token for the next load.
      if (e instanceof ApiError && e.status === 401) sessionStorage.removeItem(TOKEN_KEY);
      else sessionStorage.setItem(TOKEN_KEY, stored);
    });
  }, [applyLogin]);

  // Refresh the bearer at half its remaining life (AE-10888). A failed refresh retries on the same
  // rule (so faster as expiry nears); a 401 means the session is over.
  useEffect(() => {
    if (!token || !tokenExpiresAt) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => { timer = setTimeout(run, Math.max(5_000, (tokenExpiresAt - Date.now()) / 2)); };
    const run = () => {
      api.refresh(token).then((r) => {
        if (stopped) return;
        sessionStorage.setItem(TOKEN_KEY, r.token);
        // Token only: the webphone is already registered and a re-login would reset the call.
        dispatch({ type: 'TOKEN', token: r.token });
        setTokenExpiresAt(r.token_expires_at);
      }).catch((e) => {
        if (stopped) return;
        if (e instanceof ApiError && e.status === 401) dropSession();
        else if (Date.now() < tokenExpiresAt) schedule();
      });
    };
    schedule();
    return () => { stopped = true; clearTimeout(timer); };
  }, [token, tokenExpiresAt, dropSession]);

  // 428 from login: the roster wants a password set first (imported Vicidial agent, or an
  // admin-forced reset). The ResetPassword form collects the reset code + new password; the
  // password just typed rides along as old_password for the admin-forced case (AE-10884).
  const [pendingReset, setPendingReset] = useState<{ tenant: string; agent_id: string; password: string; campaign_id: string } | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const login = async (b: { tenant: string; agent_id: string; password: string; campaign_id: string }) => {
    dispatch({ type: 'ERROR', message: null });
    try { applyLogin(await api.login(b)); }
    catch (e) {
      if (e instanceof ApiError && e.status === 428) { setResetError(null); setPendingReset(b); return; }
      if (e instanceof ApiError && (e.status === 423 || e.status === 429)) { dispatch({ type: 'ERROR', message: e.status === 423 ? 'Account locked after too many failed logins. Try again in a few minutes.' : 'Too many attempts. Wait a minute and try again.' }); return; }
      fail(e);
    }
  };
  const completeReset = async (r: { new_password: string; reset_token?: string }) => {
    if (!pendingReset) return;
    const b = pendingReset;
    try {
      await api.setPassword({ tenant: b.tenant, agent_id: b.agent_id, new_password: r.new_password, old_password: b.password, ...(r.reset_token ? { reset_token: r.reset_token } : {}) });
      setPendingReset(null);
      applyLogin(await api.login({ ...b, password: r.new_password }));
    } catch (e2) {
      setResetError(e2 instanceof ApiError && e2.status === 403 ? 'That reset code or current password was not accepted.' : e2 instanceof Error ? e2.message : 'Could not set the password.');
    }
  };
  const logout = async () => {
    if (token) {
      try {
        await api.logout(token);
      } catch (e) {
        // 409 = agent-state refused because the agent is still on a call (AE-10798). Keep the
        // session: dropping it here strands them on a login screen they cannot get past, because
        // logging back in needs a state they can only reach by dispositioning the call first.
        if (e instanceof ApiError && e.status === 409) { fail(e); return; }
        // Anything else (BFF down, network) — the shift is over either way; drop it locally.
      }
    }
    dropSession();
  };

  // ---- transports -----------------------------------------------------------
  useEffect(() => { if (s.agentState === 'paused' || s.agentState === 'offline') setReadyIntent(false); }, [s.agentState]);
  useWebphone(webphone, wantRoom(s.config?.room_join_policy, s.agentState, readyIntent), (status) => dispatch({ type: 'WEBPHONE', status }));
  const resync = () => {
    if (!token) return;
    api.me(token)
      .then((me) => dispatch({ type: 'RESYNC', state: me.agent.state as AgentState, pauseCode: me.agent.pause_reason ?? null, reservation: me.reservation, lead: me.lead, now: Date.now() }))
      // Only a 401 means anything here; a fetch failure mid-redeploy is exactly when this runs.
      .catch((e) => { if (e instanceof ApiError && e.status === 401) dropSession(); });
  };
  // On (re)open: resync state we may have missed. On close: same call, which also turns a socket
  // the BFF refused (401: session gone) into a clean logout instead of an endless reconnect loop.
  useWs(token, (msg) => {
    if (msg.type === 'session_lost') { dropSession(); return; }
    dispatch({ type: 'WS', msg, now: Date.now() });
  }, resync, resync);

  // Customer left the room while we think we are live → X5's "customer gone" (dead) state.
  useEffect(() => {
    if (s.phase === 'live' && s.room && s.room.customers.length === 0 && s.call?.bridged_at && Date.now() - s.call.bridged_at > 4000) {
      dispatch({ type: 'CUSTOMER_GONE', now: Date.now() });
    }
  }, [s.room, s.phase, s.call?.bridged_at]);

  // ---- agent state -------------------------------------------------------------
  const setState = (r: { state: string; pause_reason?: string | null }) => dispatch({ type: 'AGENT_STATE', state: r.state as AgentState, pauseCode: r.pause_reason ?? null, now: Date.now() });
  const ready = async () => {
    if (!token) return;
    const policy = s.config?.room_join_policy;
    setReadyIntent(true);
    // on_ready: be in the room before agent-state can reserve us. If the join is slow or fails,
    // go Ready anyway after 5 s; the hook keeps retrying and the agent still sees the offer.
    if (policy === 'on_ready') {
      for (let i = 0; i < 50 && webphoneRef.current !== 'in-room'; i++) await new Promise((r) => setTimeout(r, 100));
    }
    try { setState(await api.ready(token)); }
    catch (e) { setReadyIntent(false); fail(e); }
  };
  const pause = () => {
    if (!token) return;
    setReadyIntent(false);
    if (s.config?.agent_pause_codes_active) dispatch({ type: 'MODAL', modal: 'pause' });
    else api.pause(token, 'other').then(setState).catch(fail);
  };
  const pickPause = (code: string) => { if (token) api.pause(token, code).then(setState).catch(fail); dispatch({ type: 'MODAL', modal: 'none' }); };

  // ---- call control ------------------------------------------------------------
  const verb = (v: 'hold' | 'resume' | 'mute' | 'unmute' | 'hangup') => {
    if (!token) return;
    api.callVerb(token, v).then(() => {
      if (v === 'hold' || v === 'resume') dispatch({ type: 'ROOM_ACTION', held: v === 'hold' });
      if (v === 'mute' || v === 'unmute') dispatch({ type: 'ROOM_ACTION', muted: v === 'mute' });
    }).catch(fail);
  };
  const dtmf = (digit: string) => { if (token) api.dtmf(token, digit).catch(fail); };
  const toggleRecord = () => {
    if (!token) return;
    const on = !s.call?.recording;
    (on ? api.recordStart(token) : api.recordStop(token)).then(() => dispatch({ type: 'ROOM_ACTION', recording: on })).catch(fail);
  };
  const openTransfer = () => { if (token) api.agents(token).then((r) => setAgents(r.agents)).catch(fail); dispatch({ type: 'MODAL', modal: 'transfer' }); };
  const transfer = (id: string) => { if (token) api.transfer(token, id).catch(fail); dispatch({ type: 'MODAL', modal: 'none' }); };
  const transferExternal = (number: string) => { if (token) api.transferExternal(token, number).catch(fail); dispatch({ type: 'MODAL', modal: 'none' }); };
  const threeWay = (number: string) => { if (token) api.threeWayAdd(token, { number }).catch(fail); dispatch({ type: 'MODAL', modal: 'none' }); };
  // AE-10984: internal 3-way, then drop the third party or leave (the room poll shows who is on the line).
  const threeWayAgent = (id: string) => { if (token) api.threeWayAdd(token, { target_agent_id: id }).catch(fail); dispatch({ type: 'MODAL', modal: 'none' }); };
  const dropThirdParty = (memberId: string) => { if (token) api.threeWayDrop(token, memberId).catch(fail); };
  const leaveThreeWay = () => { if (token) api.threeWayLeave(token).catch(fail); };

  // ---- disposition -----------------------------------------------------------
  const submitDispo = useCallback((code: string, comments: string, callback?: { at: number; only_me: boolean; tz: string; comments: string }) => {
    if (!token || !s.config) return;
    const status = s.config.statuses.find((x) => x.code === code);
    if (status?.scheduled_callback && !callback) { setShowCallbackForm(code); return; }
    const pauseCode = s.config.agent_pause_codes_active ? (s.config.pause_codes[0]?.code ?? 'other') : 'other';
    api.disposition(token, { code, comments, pause_after: s.pauseAfterCall, pause_code: pauseCode, ...(callback ? { callback } : {}) })
      .then((r) => {
        setShowCallbackForm(null);
        dispatch({ type: 'DISPO_DONE', state: r.agent.state as AgentState, pauseCode: r.agent.pause_reason ?? null, now: Date.now(), code, wrapupSeconds: s.pauseAfterCall ? 0 : (s.config?.wrapup_seconds ?? 0) });
        // AE-10930: the agent is free, but dialer-core had already closed the attempt — say so.
        if (r.warning) dispatch({ type: 'ERROR', message: `Disposition saved for your records, but ${r.warning}.` });
        api.callbacks(token).then((c) => setCallbacks(c.callbacks)).catch(() => undefined);
      })
      .catch((e) => { if (e instanceof ApiError && e.status === 409) dispatch({ type: 'DISPO_DONE', state: 'ready', pauseCode: null, now: Date.now(), code, wrapupSeconds: 0 }); else fail(e); });
  }, [token, s.config, s.pauseAfterCall]);

  // Wrap-up countdown
  useEffect(() => {
    if (s.phase !== 'wrapup') return;
    setWrapLeft(s.config?.wrapup_seconds ?? 0);
    const t = setInterval(() => setWrapLeft((n) => { if (n <= 1) { clearInterval(t); dispatch({ type: 'WRAPUP_OVER' }); return 0; } return n - 1; }), 1000);
    return () => clearInterval(t);
  }, [s.phase, s.config?.wrapup_seconds]);

  // Hotkeys during the live call (X5 binds them while on a call too): dispo + hangup in one key.
  // On a live call that hangs up on the customer, so it asks first (AE-10888); the same key again,
  // Enter, or the button confirms, Escape cancels. After the hangup it dispositions directly.
  const hotkeyRef = useRef(submitDispo);
  hotkeyRef.current = submitDispo;
  const [confirmHotkey, setConfirmHotkey] = useState<{ key: string; code: string } | null>(null);
  const confirmRef = useRef(confirmHotkey);
  confirmRef.current = confirmHotkey;
  const phaseRef = useRef(s.phase);
  phaseRef.current = s.phase;
  const runHotkey = useCallback((code: string) => {
    setConfirmHotkey(null);
    const go = () => hotkeyRef.current(code, '');
    // The customer may have hung up while the prompt was open: then there is nothing to hang up.
    if (phaseRef.current === 'live' && token) api.callVerb(token, 'hangup').then(go).catch(fail); else go();
  }, [token]);
  useEffect(() => {
    if (s.phase !== 'live' && s.phase !== 'hungup') { setConfirmHotkey(null); return; }
    const onKey = (e: KeyboardEvent) => {
      const pending = confirmRef.current;
      if (pending) {
        if (e.key === 'Escape') { e.preventDefault(); setConfirmHotkey(null); }
        else if (e.key === pending.key) { e.preventDefault(); runHotkey(pending.code); }
        return;
      }
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      const hk = s.config?.hotkeys.find((h) => h.key === e.key);
      if (!hk || !token) return;
      e.preventDefault();
      if (s.phase === 'live') setConfirmHotkey({ key: hk.key, code: hk.code }); else runHotkey(hk.code);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [s.phase, s.config, token, runHotkey]);

  // Lists
  useEffect(() => {
    if (!token) return;
    if (s.modal === 'callbacks') api.callbacks(token).then((c) => setCallbacks(c.callbacks)).catch(fail);
    if (s.modal === 'calllog') api.calllog(token).then((c) => dispatch({ type: 'CALLLOG', calls: c.calls })).catch(fail);
  }, [s.modal, token]);
  useEffect(() => { if (token) api.callbacks(token).then((c) => setCallbacks(c.callbacks)).catch(() => undefined); }, [token]);

  if (!s.session || !s.config) return <>
        <Login onLogin={login} error={s.error} />
        {pendingReset && <ResetPassword agentId={pendingReset.agent_id} error={resetError} onCancel={() => setPendingReset(null)} onSubmit={completeReset} />}
      </>;

  const lead = s.call?.lead ?? null;
  const closeModal = () => dispatch({ type: 'MODAL', modal: 'none' });
  return (
    <div id="x5-main-container">
      <Navbar s={s} onOpen={(m) => dispatch({ type: 'MODAL', modal: m })} onLogout={logout} />
      <LiveStatusBar s={s} onVerb={verb} onTransfer={openTransfer} onDtmf={dtmf} onRecord={toggleRecord} onDropThirdParty={dropThirdParty} onLeaveThreeWay={leaveThreeWay} />
      <div id="x5-container">
        <ContactControl s={s} onReady={ready} onPause={pause} onPauseAfter={(v) => dispatch({ type: 'PAUSE_AFTER', value: v })} onOpen={(m) => dispatch({ type: 'MODAL', modal: m })} callbackCount={callbacks.length} />
        <div id="x5-contact-main-panel" className="panel">
          <div className="tabs">
            <button className={tab === 'contact' ? 'active' : ''} onClick={() => setTab('contact')}>Contact Center</button>
            <button className={tab === 'script' ? 'active' : ''} onClick={() => setTab('script')}>Script</button>
            {(s.config.field_defs?.length ?? 0) > 0 && <button className={tab === 'forms' ? 'active' : ''} onClick={() => setTab('forms')}>Forms</button>}
            {s.config.web_form_address && lead && <button onClick={() => window.open(mergeWebForm(s.config!.web_form_address!, lead), '_blank', 'noopener')}>Web Form</button>}
          </div>
          {tab === 'contact' ? <LeadForm lead={lead} config={s.config} />
            : tab === 'forms' ? <FormsTab lead={lead} config={s.config} onSave={async (id, data) => { if (token) { await api.saveLead(token, id, data).catch(fail); } }} />
            : <ScriptTab html={s.config.script_html} lead={lead} agentName={s.session.agent_id} />}
          {s.error && <div className="error">{s.error} <button onClick={() => dispatch({ type: 'ERROR', message: null })}>dismiss</button></div>}
          {s.phase === 'hungup' && <div style={{ textAlign: 'center' }}><button className="primary" onClick={() => dispatch({ type: 'OPEN_DISPO' })}>Disposition this call</button></div>}
        </div>
      </div>

      {s.phase === 'dispo' && !showCallbackForm && (
        <DispoScreen config={s.config} selected={s.pendingDispo} onSelect={(c) => dispatch({ type: 'PENDING_DISPO', code: c || null })} onSubmit={(code, comments) => submitDispo(code, comments)} pauseAfter={s.pauseAfterCall} onPauseAfter={(v) => dispatch({ type: 'PAUSE_AFTER', value: v })} />
      )}
      {showCallbackForm && (
        <CallbackScheduler defaultTz={typeof lead?.data['timezone'] === 'string' ? (lead.data['timezone'] as string) : undefined} onCancel={() => setShowCallbackForm(null)} onSubmit={(cb) => submitDispo(showCallbackForm, cb.comments, cb)} />
      )}
      {confirmHotkey && (
        <Modal id="HotKeYConfirmBox" title="Hang up and disposition?" onClose={() => setConfirmHotkey(null)}>
          <p>Hang up on the customer and disposition this call as <b>{s.config.statuses.find((x) => x.code === confirmHotkey.code)?.name ?? confirmHotkey.code}</b>?</p>
          <div className="actions">
            <button onClick={() => setConfirmHotkey(null)}>Cancel</button>
            <button className="primary" autoFocus onClick={() => runHotkey(confirmHotkey.code)}>Hang up ({confirmHotkey.key})</button>
          </div>
        </Modal>
      )}
      {s.phase === 'wrapup' && <WrapupBox seconds={wrapLeft} onSkip={() => dispatch({ type: 'WRAPUP_OVER' })} />}
      {s.modal === 'pause' && <PauseCodePicker codes={s.config.pause_codes} onPick={pickPause} onClose={closeModal} />}
      {s.modal === 'callbacks' && <CallbacksList items={callbacks} onClose={closeModal} />}
      {s.modal === 'calllog' && <CallLog items={s.callLog} onClose={closeModal} />}
      {s.modal === 'transfer' && <TransferPanel agents={agents} onTransfer={transfer} onExternal={transferExternal} onThreeWay={threeWay} onThreeWayAgent={threeWayAgent} onClose={closeModal} />}
      {s.modal === 'manualdial' && s.config.manual_dial && s.config.manual_dial !== 'NONE' && (
        <ManualDial mode={s.config.manual_dial === 'PREVIEW_ONLY' ? 'PREVIEW_ONLY' : 'ALLOWED'} onClose={closeModal} onDial={async (b) => {
          if (!token) return;
          try { await api.manualDial(token, b); if (!b.preview) dispatch({ type: 'MODAL', modal: 'none' }); } catch (e) { fail(e); }
        }} />
      )}
    </div>
  );
}
